import React, { useState, useEffect } from "react";
import {
  ActionButtons,
  TextField,
  LoadingV2,
  AppToasterV2,
  EnumMultiSelectFieldV2,
  Footer,
  EmailField,
} from "@ucl/library";
import { useFinancialProducts } from "@/hooks/useFinancialProducts";
import { useBankerFinancialApplication } from "@/hooks/useBankerFinancialApplication";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";
import { FinancialProductReadDTO } from "@/types/FinancialApplication";
import "./NewApplicationForm.scss";
import { useAuthenticatedApiClient } from "@/hooks/useAuthenticatedApiClient";

export interface NewApplicationFormData {
  productIds: string[];
  legalFirstName: string;
  legalLastName: string;
  emailAddress: string;
  legalEntityName: string;
}

export interface NewApplicationFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (applicationId: string) => void;
}

const defaultFormData: NewApplicationFormData = {
  productIds: [],
  legalFirstName: "",
  legalLastName: "",
  emailAddress: "",
  legalEntityName: "",
};

export const NewApplicationForm: React.FC<NewApplicationFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<NewApplicationFormData>(defaultFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const financialApplicationApiClient = useAuthenticatedApiClient(FinancialApplicationApiClient);
  const { products, loading: productsLoading } = useFinancialProducts(
    financialApplicationApiClient
  );

  const { createFinancialApplicationAsBanker } = useBankerFinancialApplication();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData(defaultFormData);
      setErrors({});
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string[]> = {};

    // Validate product types
    if (!formData.productIds || formData.productIds.length === 0) {
      newErrors.productTypes = ["Product Type is required."];
    }

    // Validate legal first name
    if (!formData.legalFirstName?.trim()) {
      newErrors.legalFirstName = ["Legal First Name is required."];
    }

    // Validate legal last name
    if (!formData.legalLastName?.trim()) {
      newErrors.legalLastName = ["Legal Last Name is required."];
    }

    // Validate email address
    if (!formData.emailAddress?.trim()) {
      newErrors.emailAddress = ["Client Email Address is required."];
    } else {
      // Basic email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.emailAddress.trim())) {
        newErrors.emailAddress = ["Please enter a valid email address."];
      }
    }

    // Validate legal entity name
    if (!formData.legalEntityName?.trim()) {
      newErrors.legalEntityName = ["Legal Entity Name is required."];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Create the financial application
      const maybeFinancialApplication = await createFinancialApplicationAsBanker();

      if (!maybeFinancialApplication?.id) {
        throw new Error("Failed to create financial application");
      }

      const applicationId = maybeFinancialApplication.id;

      // Update the application with the form data
      const selectedProductIds = (formData.productIds || []) as string[];

      // Update product selections
      if (selectedProductIds.length > 0) {
        for (const selectedProductId of selectedProductIds) {
          const maybeDepositApplication = maybeFinancialApplication.depositApplications.find(
            (da) => da.financialProductId === selectedProductId
          );
          if (maybeDepositApplication) {
            await financialApplicationApiClient.toggleDepositApplicationActiveStatus(
              applicationId,
              {
                depositApplicationId: maybeDepositApplication.id,
                isActive: true,
              }
            );
          } else {
            console.error(
              `Selected product ${selectedProductId} not found in financial application`
            );
            AppToasterV2.showFailedActionToaster(
              `Selected product ${selectedProductId} not found in financial application`
            );
          }
        }
      }

      // Update basic information
      const basicInfoData = {
        personInformation: {
          firstName: formData.legalFirstName,
          lastName: formData.legalLastName,
          emailAddress: formData.emailAddress,
        },
        organizationInformation: {
          legalOrganizationName: formData.legalEntityName,
        },
      };

      await financialApplicationApiClient.updateFinancialApplication(
        applicationId,
        basicInfoData as any
      );

      AppToasterV2.showSuccessAppToaster("Application created successfully");
      onSuccess?.(applicationId);
      onClose();
    } catch (error) {
      console.error("Error creating application:", error);
      AppToasterV2.showFailedActionToaster("Failed to create application");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleFieldChange = (fieldName: keyof NewApplicationFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    // Clear field-specific errors when user starts typing
    if (errors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: [],
      }));
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="new-application-form">
      {productsLoading ? (
        <div className="new-application-form__loading">
          <LoadingV2 />
        </div>
      ) : (
        <div className="new-application-form__content">
          {/* Product Type Field */}
          <div className="new-application-form__field">
            <EnumMultiSelectFieldV2
              label="Product Type(s)"
              description="Select all products to be included in the application."
              value={formData.productIds}
              onSubmit={(value) => handleFieldChange("productIds", value)}
              errorMessages={errors.productTypes}
              growToFit={true}
              isRequired={true}
              optionValues={products.map((product: FinancialProductReadDTO) => ({
                label: product.productName,
                value: product.id,
              }))}
              placeholder="Search and select products..."
            />
          </div>

          <div className="new-application-form__fullname-container">
            {/* Legal First Name Field */}
            <div className="new-application-form__field">
              <TextField
                label="Client Legal First Name"
                description="Enter the client's legal first name."
                value={formData.legalFirstName}
                onSubmit={(value) => handleFieldChange("legalFirstName", value)}
                errorMessages={errors.legalFirstName}
                isRequired={true}
                placeholder=""
              />
            </div>

            {/* Legal Last Name Field */}
            <div className="new-application-form__field">
              <TextField
                label="Client Legal Last Name"
                description="Enter the client's legal last name."
                value={formData.legalLastName}
                onSubmit={(value) => handleFieldChange("legalLastName", value)}
                errorMessages={errors.legalLastName}
                isRequired={true}
                placeholder=""
              />
            </div>
          </div>

          {/* Client Email Address Field */}
          <div className="new-application-form__field">
            <EmailField
              label="Client Email Address"
              description="Enter the client's email address."
              value={formData.emailAddress}
              onSubmit={(value) => handleFieldChange("emailAddress", value)}
              errorMessages={errors.emailAddress}
              isRequired={true}
            />
          </div>

          {/* Legal Entity Name Field */}
          <div className="new-application-form__field">
            <TextField
              label="Client Legal Entity Name"
              description="Enter the client's legal entity name."
              value={formData.legalEntityName}
              onSubmit={(value) => handleFieldChange("legalEntityName", value)}
              errorMessages={errors.legalEntityName}
              isRequired={true}
              placeholder=""
            />
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <Footer>
        <ActionButtons
          actions={[
            {
              text: "Cancel",
              onActionClick: handleCancel,
              disabled: isSubmitting,
            },
            {
              text: "Create",
              isPrimary: true,
              onActionClick: handleSubmit,
              disabled: isSubmitting || productsLoading,
              isLoading: isSubmitting,
              loadingProgressContent: "Creating...",
              loadingSuccessContent: "Creating...",
            },
          ]}
        />
      </Footer>
    </div>
  );
};

export default NewApplicationForm;
